import {
	faArrowRightFromBracket,
	faChevronRight,
	faKey,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountSnapshotWidget } from '../components/dashboard/widgets/AccountSnapshotWidget';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { PaymentMethodsSection } from '../components/PaymentMethodsSection';
import { usePortalAuth } from '../hooks/usePortalAuth';

/**
 * Account page -- mobile-first settings/profile page.
 */
export function AccountPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { profile, activeClient, logout } = usePortalAuth();

	// Allow cart page to deep-link with ?addPayment=true to auto-open the add form
	const autoOpenAddPayment = searchParams.get('addPayment') === 'true';

	const menuItems = [
		{ label: 'Change Password', icon: faKey, path: '/account/password' },
	];

	return (
		<div className="px-4 py-4 max-w-lg mx-auto">
			<MobilePageHeader title="Account" backPath="/dashboard" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Account')}
			</h1>

			{/* User info card */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<div className="flex items-center gap-3">
					<div className="w-12 h-12 rounded-full bg-portal-accent/20 flex items-center justify-center shrink-0">
						<FontAwesomeIcon icon={faUser} className="text-portal-accent" />
					</div>
					<div className="min-w-0">
						{profile != null && (
							<>
								<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
									{t(profile.firstName)} {t(profile.lastName)}
								</div>
								<div className="text-xs text-brand-muted truncate">
									{t(profile.email)}
								</div>
							</>
						)}
						{activeClient != null && (
							<div className="text-xs text-brand-muted mt-0.5 truncate">
								{t(activeClient.name)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Account snapshot — same widget as the dashboard's Account
			    tile (open balance + available credit + credit-hold flag)
			    so customers can see their financial state without
			    bouncing back to the dashboard. The widget's Invoices /
			    Orders quick links double as the entry points for the
			    next two list pages from here. */}
			<div className="mb-4">
				<AccountSnapshotWidget />
			</div>

			{/* Payment methods */}
			<PaymentMethodsSection autoOpenAdd={autoOpenAddPayment} />

			{/* Menu items */}
			<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden mb-4 divide-y divide-gray-100 dark:divide-gray-800">
				{menuItems.map((item) => (
					<button
						key={item.path}
						type="button"
						onClick={() => navigate(item.path)}
						className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input min-h-touch"
					>
						<FontAwesomeIcon
							icon={item.icon}
							className="w-5 text-brand-muted"
						/>
						<span className="flex-1 text-left">{t(item.label)}</span>
						<FontAwesomeIcon
							icon={faChevronRight}
							className="text-xs text-gray-300 dark:text-gray-600"
						/>
					</button>
				))}
			</div>

			{/* Sign out */}
			<button
				type="button"
				onClick={logout}
				className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 bg-brand-surface border border-brand-border rounded-xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors min-h-touch"
			>
				<FontAwesomeIcon icon={faArrowRightFromBracket} />
				<span>{t('Sign Out')}</span>
			</button>
		</div>
	);
}
